/**
 * LPC-10289: C2DS Validation Error Parser
 *
 * Parses C2DS validation error messages and maps them to form field keys.
 * C2DS returns semicolon-separated error messages that need to be parsed
 * and displayed inline below the respective form fields.
 */

import { isValidRoutingNumberCheckDigit } from "./SpecificValidations";

// Field keys that can receive C2DS validation errors
export type C2DSFieldKey =
  | "routingNumber"
  | "SwiftId"
  | "IBAN"
  | "intermediarySwiftOrAba"
  | "intermediaryRoutingNumber";

// Mapping of C2DS error message patterns to field keys
// Order matters for patterns that could match multiple fields
interface C2DSErrorPattern {
  pattern: RegExp;
  fieldKey: C2DSFieldKey;
  displayName: string;
}

// Error patterns in order of specificity (more specific patterns first)
const C2DS_ERROR_PATTERNS: C2DSErrorPattern[] = [
  // Intermediary fields (check these first as they're more specific)
  {
    pattern: /Intermediary SWIFT ID/i,
    fieldKey: "intermediarySwiftOrAba",
    displayName: "Intermediary SWIFT ID",
  },
  // Main SWIFT ID (must come after Intermediary SWIFT ID check)
  {
    pattern: /(?<!Intermediary\s)SWIFT ID/i,
    fieldKey: "SwiftId",
    displayName: "SWIFT ID",
  },
  // IBAN
  {
    pattern: /IBAN/i,
    fieldKey: "IBAN",
    displayName: "IBAN",
  },
  // Transit Routing Number - this is tricky as it can appear twice
  // First occurrence = main routingNumber, second = intermediaryRoutingNumber
  {
    pattern: /Transit Routing Number/i,
    fieldKey: "routingNumber",
    displayName: "Routing Number",
  },
];

// Result of parsing C2DS errors
export interface C2DSFieldError {
  fieldKey: C2DSFieldKey;
  displayName: string;
  message: string;
}

export interface C2DSParseResult {
  fieldErrors: Record<C2DSFieldKey, C2DSFieldError | null>;
  hasErrors: boolean;
}

/**
 * LPC-10289: Options for parsing C2DS errors with routing number values
 * Used to intelligently determine which routing number field is invalid
 * when C2DS returns a single "Transit Routing Number" error
 */
export interface C2DSParseOptions {
  routingNumber?: string;
  intermediaryRoutingNumber?: string;
}

/**
 * Parses a C2DS error response string and maps errors to form fields.
 *
 * @param errorResponse - The raw error string from C2DS (semicolon-separated)
 * @param options - Optional routing number values to intelligently determine which field is invalid
 * @returns Object containing field-specific errors and a flag indicating if any errors exist
 *
 * @example
 * parseC2DSErrors("The Transit Routing Number has an invalid check digit ;SWIFT ID must be 8 or 11 characters ;", {
 *   routingNumber: "026009593",
 *   intermediaryRoutingNumber: "126009593"
 * })
 * // Returns: {
 * //   fieldErrors: {
 * //     routingNumber: null, // Valid check digit
 * //     intermediaryRoutingNumber: { fieldKey: "intermediaryRoutingNumber", displayName: "Intermediary Routing Number", message: "..." },
 * //     SwiftId: { fieldKey: "SwiftId", displayName: "SWIFT ID", message: "..." },
 * //     ...
 * //   },
 * //   hasErrors: true
 * // }
 */
export function parseC2DSErrors(errorResponse: string | null | undefined, options?: C2DSParseOptions): C2DSParseResult {
  const result: C2DSParseResult = {
    fieldErrors: {
      routingNumber: null,
      SwiftId: null,
      IBAN: null,
      intermediarySwiftOrAba: null,
      intermediaryRoutingNumber: null,
    },
    hasErrors: false,
  };

  if (!errorResponse || typeof errorResponse !== "string") {
    return result;
  }

  // Split by semicolon and filter empty strings
  const errorMessages = errorResponse
    .split(";")
    .map((msg) => msg.trim())
    .filter((msg) => msg.length > 0);

  if (errorMessages.length === 0) {
    return result;
  }

  // Track how many times we've seen "Transit Routing Number" errors
  let transitRoutingCount = 0;

  for (const message of errorMessages) {
    // Check for Transit Routing Number first (special handling for duplicates)
    if (/Transit Routing Number/i.test(message)) {
      transitRoutingCount++;

      if (transitRoutingCount === 1) {
        // LPC-10289: When we have only ONE Transit Routing Number error,
        // use check digit validation to determine which field is actually invalid
        const mainRoutingNumber = options?.routingNumber || "";
        const intermediaryRoutingNumber = options?.intermediaryRoutingNumber || "";

        const isMainValid = isValidRoutingNumberCheckDigit(mainRoutingNumber);
        const isIntermediaryValid = isValidRoutingNumberCheckDigit(intermediaryRoutingNumber);

        // If we have both values, determine which one is invalid
        if (mainRoutingNumber && intermediaryRoutingNumber) {
          if (!isMainValid && isIntermediaryValid) {
            // Only main routing number is invalid
            result.fieldErrors.routingNumber = {
              fieldKey: "routingNumber",
              displayName: "Routing Number",
              message,
            };
            result.hasErrors = true;
          } else if (isMainValid && !isIntermediaryValid) {
            // Only intermediary routing number is invalid
            result.fieldErrors.intermediaryRoutingNumber = {
              fieldKey: "intermediaryRoutingNumber",
              displayName: "Intermediary Routing Number",
              message,
            };
            result.hasErrors = true;
          } else if (!isMainValid && !isIntermediaryValid) {
            // Both are invalid - mark both
            result.fieldErrors.routingNumber = {
              fieldKey: "routingNumber",
              displayName: "Routing Number",
              message,
            };
            result.fieldErrors.intermediaryRoutingNumber = {
              fieldKey: "intermediaryRoutingNumber",
              displayName: "Intermediary Routing Number",
              message,
            };
            result.hasErrors = true;
          }
          // If both are valid (shouldn't happen), fall through to default behavior
        } else if (mainRoutingNumber && !intermediaryRoutingNumber) {
          // Only main routing number exists - it must be the invalid one
          result.fieldErrors.routingNumber = {
            fieldKey: "routingNumber",
            displayName: "Routing Number",
            message,
          };
          result.hasErrors = true;
        } else if (!mainRoutingNumber && intermediaryRoutingNumber) {
          // Only intermediary routing number exists - it must be the invalid one
          result.fieldErrors.intermediaryRoutingNumber = {
            fieldKey: "intermediaryRoutingNumber",
            displayName: "Intermediary Routing Number",
            message,
          };
          result.hasErrors = true;
        } else {
          // No routing number values provided - default to main routing number (legacy behavior)
          result.fieldErrors.routingNumber = {
            fieldKey: "routingNumber",
            displayName: "Routing Number",
            message,
          };
          result.hasErrors = true;
        }
      } else if (transitRoutingCount === 2) {
        // Second occurrence -> intermediary routing number (C2DS explicitly sent two errors)
        result.fieldErrors.intermediaryRoutingNumber = {
          fieldKey: "intermediaryRoutingNumber",
          displayName: "Intermediary Routing Number",
          message,
        };
        result.hasErrors = true;
      }
      // Ignore any additional occurrences (shouldn't happen based on requirements)
      continue;
    }

    // Check other patterns
    for (const { pattern, fieldKey, displayName } of C2DS_ERROR_PATTERNS) {
      // Skip routing number pattern as it's handled above
      if (fieldKey === "routingNumber") continue;

      if (pattern.test(message)) {
        // Only set if not already set (first match wins for each field)
        if (!result.fieldErrors[fieldKey]) {
          result.fieldErrors[fieldKey] = {
            fieldKey,
            displayName,
            message,
          };
          result.hasErrors = true;
        }
        break; // Move to next message once a match is found
      }
    }
  }

  return result;
}

/**
 * Gets the display message for a C2DS field error.
 * Format: "Incorrect [Field Name]"
 *
 * @param fieldError - The field error object
 * @returns Formatted display message or empty string if no error
 */
export function getC2DSErrorDisplayMessage(fieldError: C2DSFieldError | null): string {
  if (!fieldError) return "";

  return `Incorrect ${fieldError.displayName}`;
}

/**
 * Checks if a specific field has a C2DS error.
 *
 * @param fieldErrors - The field errors object from parseC2DSErrors
 * @param fieldKey - The field key to check
 * @returns True if the field has a C2DS error
 */
export function hasC2DSFieldError(
  fieldErrors: Record<C2DSFieldKey, C2DSFieldError | null>,
  fieldKey: C2DSFieldKey
): boolean {
  return fieldErrors[fieldKey] !== null;
}
